import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import * as Yup from 'yup';
import ReactQuill from 'react-quill-new';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useFormik } from 'formik';

import { useToastError } from '@hooks';
import { GetEventDetailedDocument, useUpdateEventMutation } from '@graphql';
import { Wrapper, WrapperContext, DialogEditContext } from './layout';

interface DescriptionProps {
  entity: 'event' | 'spot' | 'consumer';
  entityId: string;
  description?: string | null;
}

function Description(props: DescriptionProps) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('Description must be used inside DialogEditWrapper');
  }

  const { isEditMode } = context;
  const { description } = props;
  const { t } = useTranslation('spot');

  const cleanDescription = DOMPurify.sanitize(description ?? '');

  const hasMeaningfulDescription = React.useMemo(() => {
    const textContent = cleanDescription
      .replace(/&nbsp;/g, ' ')
      .replace(/<[^>]*>/g, '')
      .trim();

    return textContent.length > 0;
  }, [cleanDescription]);

  if (!isEditMode && !hasMeaningfulDescription) {
    return null;
  }

  return (
    <Wrapper type="description">
      <Wrapper.Header
        title={t('title.Description')}
        forceDisplayCta={!hasMeaningfulDescription}
        openCtaLabel={!hasMeaningfulDescription ? 'add' : 'manage'}
      />
      <Wrapper.Body disablePadding={true}>
        <DescriptionInner
          cleanDescription={cleanDescription}
          hasMeaningfulDescription={hasMeaningfulDescription}
          {...props}
        />
      </Wrapper.Body>
    </Wrapper>
  );
}

interface DescriptionInnerProps extends DescriptionProps {
  cleanDescription: string;
  hasMeaningfulDescription: boolean;
}

function DescriptionInner(props: DescriptionInnerProps) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('DescriptionInner must be used inside Wrapper');
  }

  const {
    managingType,
    isManaging,
    isSaving,
    setIsSaving,
    isSaveDisabled,
    setIsSaveDisabled,
    setIsManaging,
  } = context;
  const { entityId, hasMeaningfulDescription, cleanDescription } = props;
  const { t } = useTranslation('spot');
  const toastError = useToastError();

  const [updateEvent] = useUpdateEventMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetEventDetailedDocument, variables: { id: entityId } }],
    onCompleted() {
      setIsSaving(false);
      setIsManaging(null);
    },
    onError(error) {
      toastError(error, t('event.toast.update.error', { ns: 'field' }));
      setIsSaving(false);
      setIsManaging(null);
    },
  });

  const formik = useFormik({
    initialValues: {
      description: cleanDescription,
    },
    validationSchema: Yup.object({
      description: Yup.string()
        .label(t('event.label.description', { ns: 'field' }))
        .nullable(),
    }),
    onSubmit: (values) => {
      updateEvent({
        variables: {
          input: {
            id: entityId,
            description: DOMPurify.sanitize(values.description ?? '').replace(/&nbsp;/g, ' '),
          },
        },
      });
    },
  });

  React.useEffect(() => {
    // seek for the h3 button
    const h3Button = document.querySelector('.ql-picker-item[data-value="3"]');

    // Verify if the element exists before modifying it
    if (h3Button) {
      h3Button.setAttribute('data-label', 'Titre'); // Modifier l'étiquette
      h3Button.innerHTML = '';
    }
  }, []);

  React.useEffect(() => {
    if (isSaving) {
      formik.submitForm();
    }
  }, [isSaving]);

  React.useEffect(() => {
    if (!isManaging) {
      formik.resetForm({ values: { description: cleanDescription } });
    }
  }, [isManaging]);

  React.useEffect(() => {
    if (isSaveDisabled && formik.isValid && formik.dirty) {
      setIsSaveDisabled(false);
    } else if (!isSaveDisabled && (!formik.isValid || !formik.dirty)) {
      setIsSaveDisabled(true);
    }
  }, [isSaveDisabled, formik.isValid, formik.dirty]);

  if (isManaging === managingType) {
    return (
      <FormControl fullWidth error={formik.touched.description && Boolean(formik.errors.description)}>
        <ReactQuill
          theme="snow"
          value={formik.values.description || ''}
          onChange={(content) => {
            formik.setFieldValue('description', content);
            formik.setFieldTouched('description', content !== formik.values.description);
          }}
          placeholder={t(`event.placeholder.description`, { ns: 'field' })}
          modules={{
            toolbar: [
              [{ header: [false, 3] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['clean'],
            ],
          }}
        />
        <FormHelperText>
          {formik.touched.description && formik.errors.description
            ? String(formik.errors.description)
            : undefined}
        </FormHelperText>
      </FormControl>
    );
  }

  if (hasMeaningfulDescription) {
    return (
      <Box
        sx={{
          fontSize: { xs: '.8rem', md: '.9rem' },
          color: 'text.primary',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          '& p': {
            margin: 0,
          },
          '& p + p': {
            marginTop: '1em',
          },
          '& ul, & ol': {
            margin: 0,
            paddingLeft: '1.5rem',
          },
          '& li': {
            margin: 0,
          },
          '& span': {
            whiteSpace: 'normal !important',
          },
        }}
        dangerouslySetInnerHTML={{ __html: cleanDescription }}
      />
    );
  }

  return (
    <Wrapper.Body sx={{ p: 0 }}>
      <Typography color="text.secondary" fontSize=".8rem" fontStyle="italic">
        {t('description.event placeholder', { ns: 'field' })}
      </Typography>
    </Wrapper.Body>
  );
}

export default React.memo(Description);
